import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import equitySecuritiesService from "@equitySecurityService";
import securitiesTransactionService from "@securitiesTransactionService";

export function useFetchEquitySecurities() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['equity_securities', user?.id],
    queryFn: async () => {
      return await equitySecuritiesService.fetchAllEquitySecurities(user.id);
    },
    enabled: !!user,
  });
}

export function useCreateEquitySecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (security: Omit<EquitySecurityCreateRequest, 'user_id'>) => {
      return await equitySecuritiesService.createEquitySecurity({...security, userId: user.id});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equity_securities'] }),
  });
}

export function useUpdateEquitySecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (req: EquitySecurityUpdateRequest) => {
      return await equitySecuritiesService.updateEquitySecurity({...req, userId: user.id});
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equity_securities'] }),
  });
}

export function useDeleteEquitySecurity() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      return await equitySecuritiesService.deleteEquitySecurity({ id, userId: user.id });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equity_securities'] }),
  });
}

export function useEquitySecurityDetail(id: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['equity_security_detail', id, user?.id],
    queryFn: async () => {
      // Fetch the equity security
      const security = await equitySecuritiesService.fetchEquitySecurityById({ id, userId: user.id });
      // TODO: Look into the possibility of moving this to be fetched alongside the equity securities to avoid round trips
      //  basically fetch it in one go
      const transactions = await securitiesTransactionService.fetchSecurityTransactionsForSecurity({
        securityId: security.id,
        securityType: 'equity',
        userId: user.id
      });

      return {
        ...security,
        transactions: transactions || [],
      };
    },
    enabled: !!user && !!id,
  });
}
